import React, { Suspense, lazy, useEffect } from 'react';
import { Route, Switch, Redirect } from 'react-router';
import { QueryClient, QueryClientProvider } from 'react-query';
import { IntlProvider } from 'react-intl';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { light_theme } from '@twicapp/ui/constants/themes';
import ThemeProvider from '@twicapp/ui/components/ThemeProvider';
import BaseStyle from '@twicapp/ui/components/legacy/BaseStyle';

//Utils
import * as Amplitude from 'utils/amplitude';
import * as AuthUtils from 'utils/auth';
import * as config from 'utils/config';
import * as Bugsnag from 'utils/bugsnag';

//Contexts
import { FeatureFlagContextProvider } from 'contexts/FeatureFlagContext';
import { ProfileContextProvider } from 'contexts/ProfileContext';
import { PretaxContextProvider } from 'contexts/PretaxContext';

//Components
import BaseLayout from 'components/layouts/BaseLayout';
import WelcomeLayout from 'components/layouts/WelcomeLayout';
import StoreLayout from 'components/layouts/StoreLayout';
import CardsLayout from 'components/layouts/CardsLayout';
import SettingsLayout from 'components/layouts/SettingsLayout';
import ProfileLayout from 'components/layouts/ProfileLayout';
import EnrollLayout from 'components/layouts/EnrollLayout';
import LoginLayout from 'components/layouts/LoginLayout';

import ScrollToTop from 'components/common/ScrollToTop';
import Loader from 'components/utility/loader';

//IMP Moving this to lazy loading breaks dropdown and intercom product tour.
//Lazy loading skip components start
//Settings
import SettingsProfile from 'components/SettingsProfile';
// Profile
import ProfileSubscriptionList from 'components/ProfileSubscriptionList';
// Lazy loading causes search input to lose focus when the first
// character is typed and the route redirects to /search
import ProductSearchResultsPage from 'components/ProductSearchResults';
//Lazy loading skip components ends

const LoginPage = lazy(
  () => import(/* webpackChunkName: "login" */ 'components/Login')
);
const LoginPasswordResetPage = lazy(
  () =>
    import(
      /* webpackChunkName: "login_password_reset" */ 'components/LoginPasswordReset'
    )
);
const Authentication = lazy(
  () => import(/* webpackChunkName: "auth" */ 'components/Authentication')
);

const WelcomeEmployerSponsoredPage = lazy(
  () =>
    import(
      /* webpackChunkName: "welcome_employer_sponsored" */ 'components/WelcomeEmployerSponsored'
    )
);

const WelcomePretaxPage = lazy(
  () =>
    import(/* webpackChunkName: "welcome_pretax" */ 'components/WelcomePretax')
);

const WelcomeWaysToSpendPage = lazy(
  () =>
    import(
      /* webpackChunkName: "welcome_ways_to_spend" */ 'components/WelcomeWaysToSpend'
    )
);

const WelcomeFinishAccountPage = lazy(
  () =>
    import(
      /* webpackChunkName: "welcome_finish_account" */ 'components/WelcomeFinishAccount'
    )
);

const WelcomeSetUpPasswordPage = lazy(
  () =>
    import(
      /* webpackChunkName: "welcome_set_up_password" */ 'components/WelcomeSetUpPassword'
    )
);

const ClaimNewPage = lazy(
  () => import(/* webpackChunkName: "claim_new" */ 'components/ClaimNew')
);
const ClaimSuccessPage = lazy(
  () =>
    import(/* webpackChunkName: "claim_success" */ 'components/ClaimSuccess')
);
const ClaimListPage = lazy(
  () => import(/* webpackChunkName: "claim_list" */ 'components/ClaimList')
);
const ClaimDetailEmployerSponsoredPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claim_detail" */ 'components/ClaimDetailEmployerSponsored'
    )
);
const ClaimDetailPretaxClaimPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claim_detail_pretax_claim" */ 'components/ClaimDetailPretaxClaim'
    )
);
const ClaimDetailPretaxTransactionPage = lazy(
  () =>
    import(
      /* webpackChunkName: "claim_detail_pretax_transaction" */ 'components/ClaimDetailPretaxTransaction'
    )
);

const ProfileOrderListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "profile_orders" */ 'components/ProfileOrderList'
    )
);

//Vendors
const VendorProductDetailPage = lazy(
  () =>
    import(/* webpackChunkName: "product" */ 'components/VendorProductDetail')
);
const VendorVenueSearchPage = lazy(
  () => import(/* webpackChunkName: "venue" */ 'components/VendorVenueSearch')
);
//Pretax Accounts Store Links
const PretaxStoreDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "pretax_store_details" */ 'components/PretaxStoreDetail'
    )
);
const ExternalStoreLinkPage = lazy(
  () =>
    import(
      /* webpackChunkName: "external_store_link" */ 'components/ExternalStoreLink'
    )
);

const AccountListPage = lazy(
  () =>
    import(/* webpackChunkName: "account_overview" */ 'components/AccountList')
);

const AccountDetailEmployerSponsored = lazy(
  () =>
    import(
      /* webpackChunkName: "wallet" */ 'components/AccountDetailEmployerSponsored'
    )
);

const AccountDetailPretaxPage = lazy(
  () =>
    import(/* webpackChunkName: "wallet" */ 'components/AccountDetailPretax')
);

const AccountEnrollPretaxPage = lazy(
  () =>
    import(/* webpackChunkName: "wallet" */ 'components/AccountEnrollPretax')
);
const AccountEnrollReviewPretaxPage = lazy(
  () =>
    import(
      /* webpackChunkName: "wallet" */ 'components/AccountEnrollReviewPretax'
    )
);
const AccountEnrollConfirmedPretaxPage = lazy(
  () =>
    import(
      /* webpackChunkName: "wallet" */ 'components/AccountEnrollConfirmedPretax'
    )
);

//Transaction
const ProfileOrderDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "transaction" */ 'components/ProfileOrderDetail'
    )
);
//CardTransaction
const TransactionDetailTwicCardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "transaction" */ 'components/TransactionDetailTwicCard'
    )
);

//Subscription
const ProfileSubscriptionDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "subscription" */ 'components/ProfileSubscriptionDetail'
    )
);

const SettingsPaymentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_payment" */ 'components/SettingsPayment'
    )
);
const SettingsIntegrationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_integrations" */ 'components/SettingsIntegration'
    )
);
const SettingsNotificationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_integrations" */ 'components/SettingsNotification'
    )
);
const SettingsSecurityPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_integrations" */ 'components/SettingsSecurity'
    )
);
const SettingsTwicCardsNewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsTwicCardsNew'
    )
);
const SettingsTwicCardsListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsTwicCardsList'
    )
);

const SettingsTwicCardReplacePage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsTwicCardsReplace'
    )
);
const SettingsTwicCardsEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsTwicCardsEdit'
    )
);
const SettingsTwicCardsSuccessPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsTwicCardsSuccess'
    )
);

const SettingsPretaxCardNewPage = lazy(
  () =>
    import(/* webpackChunkName: "wallet" */ 'components/SettingsPretaxCardNew')
);
const SettingsPretaxCardListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsPretaxCardList'
    )
);
const SettingsPretaxDependentPage = lazy(
  () =>
    import(
      /* webpackChunkName: "settings_card" */ 'components/SettingsPretaxDependent'
    )
);

//Checkout
const CheckoutPage = lazy(
  () => import(/* webpackChunkName: "checkout" */ 'components/Checkout')
);

//Recognition
const RecognitionDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: "recognition_details" */ 'components/RecognitionDetail'
    )
);
const RecognitionListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "recognition_list" */ 'components/RecognitionList'
    )
);
const RecognitionPresentationPage = lazy(
  () =>
    import(
      /* webpackChunkName: "recognition_presentation" */ 'components/RecognitionPresentation'
    )
);

//Slack auth
const SlackAuthPage = lazy(
  () => import(/* webpackChunkName: "slack" */ 'components/SlackAuth')
);
const SlackInstallPage = lazy(
  () =>
    import(/* webpackChunkName: "slack_install" */ 'components/SlackInstall')
);
const HomePage = lazy(
  () => import(/* webpackChunkName: "home" */ 'components/Home')
);

const SAMLPage = lazy(
  () => import(/* webpackChunkName: "saml" */ 'components/Saml')
);

// products
const ProductListExplorePage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_explore" */ 'components/ProductListExplore'
    )
);
const ProductListCategoryPage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_category" */ 'components/ProductListCategory'
    )
);
const ProductListFeaturedPage = lazy(
  () =>
    import(
      /* webpackChunkName: "product_list_featured" */ 'components/ProductListFeatured'
    )
);

const VendorListVenuesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "vendor_list_venues" */ 'components/VendorListVenues'
    )
);

const ErrorFallbackPage = lazy(
  () =>
    import(/* webpackChunkName: "error_fallback" */ 'components/ErrorFallback')
);

Bugsnag.start();
const BugsnagErrorBoundary = Bugsnag.createErrorBoundary();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

// @ts-expect-error ts-migrate(7031) FIXME: Binding element 'Component' implicitly has an 'any... Remove this comment to see the full error message
const PrivateRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    /*eslint-disable no-return-assign*/
    render={(props) =>
      AuthUtils.isAuthCookiePresent() ? (
        <Component {...props} />
      ) : (
        (window.location.href = config.getLogInRedirectUrl())
      )
    }
  />
);

const routesWithEnrollLayout = () => (
  <Route
    exact
    path={[
      '/accounts/pretax/commuter-enroll',
      '/accounts/pretax/commuter-enroll-review',
      '/accounts/pretax/commuter-enroll-confirmed',
    ]}
  >
    <ProfileContextProvider>
      <PretaxContextProvider>
        <BaseLayout>
          <EnrollLayout>
            <PrivateRoute
              exact
              path="/accounts/pretax/commuter-enroll"
              component={AccountEnrollPretaxPage}
            />
            <PrivateRoute
              exact
              path="/accounts/pretax/commuter-enroll-review"
              component={AccountEnrollReviewPretaxPage}
            />
            <PrivateRoute
              exact
              path="/accounts/pretax/commuter-enroll-confirmed"
              component={AccountEnrollConfirmedPretaxPage}
            />
          </EnrollLayout>
        </BaseLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);

const routesWithStoreLayout = () => (
  <Route
    exact
    path={[
      '/explore',
      '/explore/accounts/:employee_wallet_id',
      '/explore/accounts/:employee_wallet_id/products/:category',
      '/explore/accounts/:employee_wallet_id/featured/:featured_section',
      '/explore/pretax_accounts/:pretax_account',
      '/explore/gym-and-fitness',
      '/search',
    ]}
  >
    <ProfileContextProvider>
      <PretaxContextProvider>
        <BaseLayout>
          <StoreLayout>
            <PrivateRoute
              exact
              path="/explore/accounts/:employee_wallet_id/products/:category"
              component={ProductListCategoryPage}
            />
            <PrivateRoute
              exact
              path="/explore/accounts/:employee_wallet_id/featured/:featured_section"
              component={ProductListFeaturedPage}
            />
            <PrivateRoute
              exact
              path="/explore"
              component={ProductListExplorePage}
            />
            <PrivateRoute
              exact
              path="/explore/pretax_accounts/:pretax_account"
              component={PretaxStoreDetailPage}
            />
            <PrivateRoute
              exact
              path="/explore/gym-and-fitness"
              component={VendorListVenuesPage}
            />
            <PrivateRoute
              exact
              path="/search"
              component={ProductSearchResultsPage}
            />
          </StoreLayout>
        </BaseLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);

const routesWithWelcomeLayout = () => (
  <Route
    exact
    path={[
      '/welcome/employer-sponsored',
      '/welcome/pretax',
      '/welcome/ways-to-spend',
      '/welcome/finish-account',
      '/welcome/set-up-password',
    ]}
  >
    <ProfileContextProvider>
      <PretaxContextProvider>
        <WelcomeLayout>
          <PrivateRoute
            exact
            path="/welcome/employer-sponsored"
            component={WelcomeEmployerSponsoredPage}
          />
          <PrivateRoute
            exact
            path="/welcome/pretax"
            component={WelcomePretaxPage}
          />
          <PrivateRoute
            exact
            path="/welcome/ways-to-spend"
            component={WelcomeWaysToSpendPage}
          />
          <PrivateRoute
            exact
            path="/welcome/finish-account"
            component={WelcomeFinishAccountPage}
          />
          <PrivateRoute
            exact
            path="/welcome/set-up-password"
            component={WelcomeSetUpPasswordPage}
          />
        </WelcomeLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);

const routesWithCardSettingsLayout = () => (
  <Route
    exact
    path={[
      '/settings/employer-sponsored/cards',
      '/settings/employer-sponsored/cards/settings',
    ]}
  >
    <ProfileContextProvider>
      <PretaxContextProvider>
        <BaseLayout>
          <SettingsLayout>
            <CardsLayout>
              <PrivateRoute
                exact
                path="/settings/employer-sponsored/cards"
                component={SettingsTwicCardsListPage}
              />
              <PrivateRoute
                exact
                path="/settings/employer-sponsored/cards/settings"
                component={SettingsTwicCardsEditPage}
              />
            </CardsLayout>
          </SettingsLayout>
        </BaseLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);

const routesWithSettingsLayout = () => (
  <Route
    exact
    path={[
      '/settings',
      '/settings/profile',
      '/settings/payment',
      '/settings/pretax/cards',
      '/settings/pretax/cards/new',
      '/settings/employer-sponsored/cards/new',
      '/settings/employer-sponsored/cards/:card_id/replace',
      '/settings/pretax/dependents/:dependent_id',
      '/settings/bank-accounts',
      '/settings/integrations',
      '/settings/notifications',
      '/settings/security',
    ]}
  >
    <ProfileContextProvider>
      <PretaxContextProvider>
        <BaseLayout>
          <SettingsLayout>
            <PrivateRoute
              exact
              path="/settings"
              component={(props: $TSFixMe) => (
                <Redirect to={`/settings/profile${props.location.search}`} />
              )}
            />
            <PrivateRoute
              exact
              path="/settings/profile"
              component={SettingsProfile}
            />
            <PrivateRoute
              exact
              path="/settings/payment"
              component={SettingsPaymentPage}
            />
            <PrivateRoute
              exact
              path="/settings/employer-sponsored/cards/new"
              component={SettingsTwicCardsNewPage}
            />
            <PrivateRoute
              exact
              path="/settings/pretax/cards"
              component={SettingsPretaxCardListPage}
            />
            <PrivateRoute
              exact
              path="/settings/pretax/cards/new"
              component={SettingsPretaxCardNewPage}
            />
            <PrivateRoute
              exact
              path="/settings/pretax/dependents/:dependent_id"
              component={SettingsPretaxDependentPage}
            />
            <PrivateRoute
              exact
              path="/settings/bank-accounts"
              component={SettingsPaymentPage}
            />
            <PrivateRoute
              exact
              path="/settings/integrations"
              component={SettingsIntegrationPage}
            />
            <PrivateRoute
              exact
              path="/settings/notifications"
              component={SettingsNotificationPage}
            />
            <PrivateRoute
              exact
              path="/settings/security"
              component={SettingsSecurityPage}
            />

            <PrivateRoute
              exact
              path="/settings/employer-sponsored/cards/:card_id/replace"
              component={SettingsTwicCardReplacePage}
            />
          </SettingsLayout>
        </BaseLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);

const routesWithProfileLayout = () => (
  <Route exact path={['/history/subscriptions', '/history/orders']}>
    <ProfileContextProvider>
      <PretaxContextProvider>
        <BaseLayout>
          <ProfileLayout>
            <PrivateRoute
              exact
              path="/history/subscriptions"
              component={ProfileSubscriptionList}
            />
            <PrivateRoute
              exact
              path="/history/orders"
              component={ProfileOrderListPage}
            />
          </ProfileLayout>
        </BaseLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);

const routesWithBaseLayout = () => (
  <Route
    exact
    path={[
      '/',
      '/home',
      '/explore/search',
      '/explore/:vendor_id/:product_id',
      '/accounts',
      '/accounts/more-info',
      '/accounts/pretax/:flex_account_id',
      '/accounts/employer-sponsored/:wallet_id',
      '/claims',
      '/claims/employer-sponsored/:claim_id',
      '/claims/pretax-claim/:claim_id',
      '/claims/pretax-transaction/:transaction_id',
      '/claims/new',
      '/claims/success',
      '/history',
      '/orders/:transaction_id',
      '/subscriptions/:subscription_id/payments-history',
      '/subscriptions/:subscription_id/manage-membership',
      '/orders/twic-card-transactions/:id',
      '/checkout',
      '/recognition',
      '/recognition/:recognition_id',
      '/settings/employer-sponsored/cards/:stripe_card_id/success',
      '/slack/auth',
      '/slack/install',
    ]}
  >
    <ProfileContextProvider>
      <PretaxContextProvider>
        <BaseLayout>
          <Switch>
            {/*/!*Authenticated routes*!/*/}
            <PrivateRoute exact path="/" component={HomePage} />
            <PrivateRoute exact path="/home" component={HomePage} />
            <PrivateRoute
              exact
              path="/explore/search"
              component={VendorVenueSearchPage}
            />
            <PrivateRoute
              exact
              path="/explore/:vendor_id/:product_id"
              component={VendorProductDetailPage}
            />
            <PrivateRoute exact path="/accounts" component={AccountListPage} />
            <PrivateRoute
              exact
              path="/accounts/more-info"
              component={AccountListPage}
            />
            <PrivateRoute
              exact
              path="/accounts/pretax/:flex_account_id"
              component={AccountDetailPretaxPage}
            />
            <PrivateRoute
              exact
              path="/accounts/employer-sponsored/:wallet_id"
              component={AccountDetailEmployerSponsored}
            />
            <PrivateRoute exact path="/claims" component={ClaimListPage} />
            <PrivateRoute
              exact
              path="/claims/employer-sponsored/:claim_id"
              component={ClaimDetailEmployerSponsoredPage}
            />
            <PrivateRoute
              exact
              path="/claims/pretax-claim/:claim_id"
              component={ClaimDetailPretaxClaimPage}
            />
            <PrivateRoute
              exact
              path="/claims/pretax-transaction/:transaction_id"
              component={ClaimDetailPretaxTransactionPage}
            />
            <PrivateRoute exact path="/claims/new" component={ClaimNewPage} />
            <PrivateRoute
              exact
              path="/claims/success"
              component={ClaimSuccessPage}
            />

            <Route exact path="/history">
              <Redirect to="/history/subscriptions" />
            </Route>

            <PrivateRoute
              exact
              path="/orders/:transaction_id"
              component={ProfileOrderDetail}
            />

            <PrivateRoute
              exact
              path="/subscriptions/:subscription_id/payments-history"
              component={ProfileSubscriptionDetail}
            />

            <PrivateRoute
              exact
              path="/subscriptions/:subscription_id/manage-membership"
              component={ProfileSubscriptionDetail}
            />

            <PrivateRoute
              exact
              path="/orders/twic-card-transactions/:id"
              component={TransactionDetailTwicCardPage}
            />

            <PrivateRoute exact path="/checkout" component={CheckoutPage} />
            <PrivateRoute
              exact
              path="/recognition"
              component={RecognitionListPage}
            />
            <PrivateRoute
              exact
              path="/recognition/:recognition_id"
              component={RecognitionDetailPage}
            />
            <PrivateRoute
              exact
              path="/settings/employer-sponsored/cards/:stripe_card_id/success"
              component={SettingsTwicCardsSuccessPage}
            />
            <PrivateRoute exact path="/slack/auth" component={SlackAuthPage} />
            <PrivateRoute
              exact
              path="/slack/install"
              component={SlackInstallPage}
            />
          </Switch>
        </BaseLayout>
      </PretaxContextProvider>
    </ProfileContextProvider>
  </Route>
);
const routesWithNoLayout = () => (
  <Route
    exact
    path={[
      '/saml/alegeus',
      '/external_store/:account_type/:store_name',
      '/recognition/presentation',
    ]}
  >
    <ProfileContextProvider>
      <PrivateRoute
        exact
        path="/external_store/:account_type/:store_name"
        component={ExternalStoreLinkPage}
      />
      <PrivateRoute
        exact
        path="/recognition/presentation"
        component={RecognitionPresentationPage}
      />
      <PrivateRoute exact path="/saml/alegeus" component={SAMLPage} />
    </ProfileContextProvider>
  </Route>
);

const publicRoutes = () => (
  <Route exact path={['/login', '/login/password-reset', '/auth/:type']}>
    <LoginLayout>
      <Route exact path="/login" component={LoginPage} />
      <Route
        exact
        path="/login/password-reset"
        component={LoginPasswordResetPage}
      />
      <Route exact path="/auth/:type" component={Authentication} />
    </LoginLayout>
  </Route>
);

const RouterListener = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useEffect(() => {
    const { pathname, search } = location;
    Amplitude.logEvent(Amplitude.EVENTS.PageViewed, {
      pathname,
      search,
    });
  }, [location]);

  return <>{children}</>;
};

const Routes = () => (
  <BugsnagErrorBoundary>
    <FeatureFlagContextProvider>
      <IntlProvider locale={'en'}>
        <ThemeProvider theme={light_theme}>
          <QueryClientProvider client={queryClient}>
            <BaseStyle />
            <BrowserRouter>
              <RouterListener>
                <ScrollToTop />
                <Suspense fallback={<Loader />}>
                  <ErrorBoundary FallbackComponent={ErrorFallbackPage}>
                    <Switch>
                      {publicRoutes()}
                      {routesWithEnrollLayout()}
                      {routesWithWelcomeLayout()}
                      {routesWithStoreLayout()}
                      {routesWithSettingsLayout()}
                      {routesWithCardSettingsLayout()}
                      {routesWithNoLayout()}
                      {routesWithBaseLayout()}
                      {routesWithProfileLayout()}
                      {/* Catch all route MUST be last */}
                      <Route component={ErrorFallbackPage} />
                    </Switch>
                  </ErrorBoundary>
                </Suspense>
              </RouterListener>
            </BrowserRouter>
            {/* <ReactQueryDevtools /> */}
          </QueryClientProvider>
        </ThemeProvider>
      </IntlProvider>
    </FeatureFlagContextProvider>
  </BugsnagErrorBoundary>
);

export default Routes;
